import React, { useEffect, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Tooltip,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AssignmentOutlinedIcon from "@material-ui/icons/AssignmentOutlined";
import ChatBubbleOutlineIcon from "@material-ui/icons/ChatBubbleOutline";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import { useHistory } from "react-router-dom";

import toastError from "../../errors/toastError";
import api from "../../services/api";
import OsInteractionDialog from "../OsInteractionDialog";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(1),
    marginTop: theme.spacing(1),
    flexShrink: 0
  },
  heading: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5)
  },
  card: {
    width: "100%",
    padding: theme.spacing(0.75, 1),
    marginTop: theme.spacing(0.75),
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.action.hover
    }
  },
  content: {
    minWidth: 0,
    flex: 1
  },
  title: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    flexWrap: "wrap"
  },
  description: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  actions: {
    display: "flex",
    alignItems: "center",
    flexShrink: 0
  }
}));

const TicketOsSummary = ({ ticket, enabled = true }) => {
  const classes = useStyles();
  const history = useHistory();
  const ticketId = ticket?.id;
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [interactionOrder, setInteractionOrder] = useState(null);
  const [objectOptions, setObjectOptions] = useState(null);

  useEffect(() => {
    if (!enabled || !ticketId) return undefined;
    let active = true;
    setLinks([]);

    const loadLinks = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/tickets/${ticketId}/os`);
        if (active) setLinks(data.data || []);
      } catch {
        if (active) setLinks([]);
      } finally {
        if (active) setLoading(false);
      }
    };

    const handleUpdate = event => {
      if (event.detail?.ticketId === ticketId) loadLinks();
    };

    loadLinks();
    window.addEventListener("ticket-os-links-updated", handleUpdate);
    return () => {
      active = false;
      window.removeEventListener("ticket-os-links-updated", handleUpdate);
    };
  }, [enabled, ticketId, reloadKey]);

  const openInteraction = async (event, order) => {
    event.stopPropagation();
    if (!order) return;

    setInteractionOrder(order);
    if (objectOptions !== null) return;

    try {
      const { data } = await api.get("/os/objetos-conclusao", {
        params: { per_page: 200 }
      });
      setObjectOptions(data.data || []);
    } catch (err) {
      setObjectOptions([]);
      toastError(err);
    }
  };

  const handleInteractionSaved = () => {
    setInteractionOrder(null);
    setReloadKey(current => current + 1);
  };

  if (!loading && !links.length) return null;

  return (
    <>
      <Paper className={classes.root} square variant="outlined">
        <Box className={classes.heading}>
          <AssignmentOutlinedIcon fontSize="small" />
          <Typography variant="subtitle1">Vinculadas ao atendimento</Typography>
          {loading && <CircularProgress size={14} />}
        </Box>

        {links.map(link => {
          const order = link.ordem;
          return (
            <Paper
              key={link.id}
              className={classes.card}
              variant="outlined"
              onClick={() => history.push(`/ordens-servico/${link.osId}`)}
            >
              <Box className={classes.content}>
                <Box className={classes.title}>
                  <Typography variant="subtitle2">OS #{link.osId}</Typography>
                  {order?.status?.nome && (
                    <Chip
                      label={order.status.nome}
                      size="small"
                      color={order.data_fechada ? "default" : "primary"}
                    />
                  )}
                </Box>
                <Typography
                  className={classes.description}
                  variant="caption"
                  display="block"
                >
                  {order?.descricao_curta || "Detalhes indisponíveis"}
                </Typography>
                {order && (
                  <Typography
                    className={classes.description}
                    variant="caption"
                    color="textSecondary"
                    display="block"
                  >
                    {order.tecnico?.nome || "Sem técnico"} ·{" "}
                    {order.prioridade?.nome || "Sem prioridade"}
                  </Typography>
                )}
              </Box>

              <Box className={classes.actions}>
                <Tooltip title="Interar nesta OS">
                  <span>
                    <IconButton
                      size="small"
                      disabled={!order}
                      onClick={event => openInteraction(event, order)}
                      aria-label={`Interar na OS ${link.osId}`}
                    >
                      <ChatBubbleOutlineIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Abrir detalhes da OS">
                  <IconButton
                    size="small"
                    aria-label={`Abrir OS ${link.osId}`}
                  >
                    <ChevronRightIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Paper>
          );
        })}
      </Paper>

      <OsInteractionDialog
        open={Boolean(interactionOrder)}
        onClose={() => setInteractionOrder(null)}
        onSaved={handleInteractionSaved}
        order={interactionOrder}
        objectOptions={objectOptions || []}
        canFinalize={!interactionOrder?.data_fechada}
      />
    </>
  );
};

export default TicketOsSummary;
